/**
 * ApiHubPage — Hub unificado para todo lo relacionado a APIs e integraciones.
 *
 * Reemplaza el antiguo módulo "API Generator" y consolida lo que vivía disperso:
 *   🔑 API Keys           — antes en SettingsPage > tab "api"
 *   ⚡ Generador de APIs  — antes /endpoint-builder (ApiGeneratorPage)
 *   🔗 Constructor UTM    — nuevo
 *
 * Sigue el patrón "Team Config" (tabs + tooltips + deep-link por ?tab=).
 * Las rutas viejas (/endpoint-builder, /settings?tab=api) redirigen aquí.
 */
import { useEffect, useState } from 'react';
import { APP_NAME } from '@/lib/brand';
import { useSearchParams, Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Key, Wand2, Link2, Mail, BookOpen, ExternalLink } from 'lucide-react';
import ApiKeysManager from '@/components/api-hub/ApiKeysManager';
import InboundEmailTab from '@/components/api-hub/InboundEmailTab';
import UtmBuilder from '@/components/api-hub/UtmBuilder';
import ApiGeneratorPage from '@/pages/ApiGeneratorPage';
import BigTooltip from '@/components/team-config/BigTooltip';

const TAB_DEFS = [
  {
    key: 'keys',
    label: 'API Keys',
    icon: Key,
    tooltip: {
      title: 'API Keys',
      body: 'Genera claves secretas para que otros sistemas (Zapier, n8n, tu web, scripts) puedan leer y escribir leads/propiedades en tu cuenta sin necesidad de usuario y contraseña.',
      useCase: 'Conectas Zapier para que cada vez que llegue un lead desde un anuncio de Facebook, se cree automáticamente en {APP_NAME}. Generas una clave llamada "Zapier - Producción" y la pegas en Zapier.',
      academySearch: 'api keys integracion',
    },
  },
  {
    key: 'builder',
    label: 'Generador de APIs',
    icon: Wand2,
    tooltip: {
      title: 'Generador de APIs personalizadas',
      body: 'Crea endpoints CRUD propios sin tocar código. Defines una entidad ("Visitas", "Citas", "Encuestas") con sus campos y automáticamente tienes /api/custom/{slug} con GET, POST, PUT, DELETE listos.',
      useCase: 'Tu equipo necesita registrar "Visitas a inmueble" con campos: cliente, fecha, comentario. Creas la entidad y al instante puedes integrar ese formulario desde cualquier app externa.',
      academySearch: 'generador api entidades custom',
    },
  },
  {
    key: 'email',
    label: 'Leads por correo',
    icon: Mail,
    tooltip: {
      title: 'Leads que llegan por correo',
      body: 'Los portales inmobiliarios (Lamudi, Inmuebles24, Vivanuncios, EasyBroker) te mandan cada lead por email. Aquí obtienes una dirección única de tu empresa: reenvías esos correos ahí y {APP_NAME} los recibe.',
      useCase: 'Lamudi te manda "Nuevo contacto: María, 55-1234-5678, interesada en Grand Montessino". Configuras el reenvío automático una vez y ese correo cae aquí. Por ahora solo se recibe y se muestra; la creación automática del lead llega en la siguiente fase.',
      academySearch: 'leads por correo portales reenvio',
    },
  },
  {
    key: 'utm',
    label: 'Constructor UTM',
    icon: Link2,
    tooltip: {
      title: 'Constructor de URLs trackeables',
      body: 'Genera URLs con parámetros UTM para saber de dónde viene cada visita (email, Facebook, WhatsApp, Google, etc.). Sin UTMs no puedes medir qué campaña funcionó.',
      useCase: 'Vas a enviar un email masivo con un link a una landing. Generas la URL con utm_source=newsletter y utm_campaign=mayo-2026, la copias y la pegas en el email. Después en Google Analytics ves cuántos leads vinieron de ahí.',
      academySearch: 'utm builder campañas tracking',
    },
  },
];

export default function ApiHubPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'keys';
  const [activeTab, setActiveTab] = useState(TAB_DEFS.find(t => t.key === initialTab) ? initialTab : 'keys');

  useEffect(() => {
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, setSearchParams]);

  return (
    <div className="space-y-6" data-testid="api-hub-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">API Hub</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1 max-w-2xl">
            Todo lo que necesitas para conectar {APP_NAME} con el resto de tu stack: <span className="text-violet-300">claves de acceso</span>,
            <span className="text-cyan-300"> endpoints personalizados</span> y <span className="text-amber-300">URLs trackeables (UTMs)</span> para tus campañas.
          </p>
        </div>
        <Link
          to="/developers"
          target="_blank"
          className="inline-flex items-center gap-2 text-xs text-cyan-300 hover:text-cyan-200 border border-cyan-400/30 hover:border-cyan-400/60 rounded-full px-4 py-2 transition"
          data-testid="api-hub-docs-link"
        >
          <BookOpen className="w-3.5 h-3.5" /> Documentación REST completa <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#1F2833]/80 border border-white/5 mb-5 flex flex-wrap h-auto" data-testid="api-hub-tabs">
          {TAB_DEFS.map(t => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.key}
                value={t.key}
                className="data-[state=active]:bg-violet-400/15 data-[state=active]:text-violet-300 text-xs gap-1.5"
                data-testid={`api-hub-tab-${t.key}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
                <BigTooltip {...t.tooltip} side="right" />
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="keys">{activeTab === 'keys' && <ApiKeysManager />}</TabsContent>
        <TabsContent value="builder">
          {activeTab === 'builder' && (
            <Card className="bg-transparent border-0 shadow-none">
              <CardContent className="p-0">
                <ApiGeneratorPage />
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="email">{activeTab === 'email' && <InboundEmailTab />}</TabsContent>
        <TabsContent value="utm">{activeTab === 'utm' && <UtmBuilder />}</TabsContent>
      </Tabs>
    </div>
  );
}
